import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>
        
        <div className="space-y-6 text-gray-700">
          <p className="text-sm text-gray-500">Last Updated: {new Date().toLocaleDateString()}</p>
          
          <section>
            <h2 className="text-2xl font-semibold mb-3">1. Agreement to Terms</h2>
            <p>
              By accessing and using I-Match CRM ("the Service"), operated by Vinculum ("Company", "we", "us", or "our"), 
              you agree to be bound by these Terms of Service. If you disagree with any part of these terms, 
              you may not access the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">2. Description of Service</h2>
            <p>
              I-Match CRM is a customer relationship management platform that helps businesses manage leads, 
              contacts, companies, and sales processes. The Service includes AI-powered enrichment features, 
              task management, calendar integration, and reporting capabilities.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">3. User Accounts</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You must be at least 16 years old to use this Service</li>
              <li>You are responsible for maintaining the security of your account</li>
              <li>You are responsible for all activities that occur under your account</li>
              <li>You must provide accurate and complete information</li>
              <li>You must notify us immediately of any unauthorized access</li>
              <li>We reserve the right to refuse service or terminate accounts</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">4. Acceptable Use</h2>
            <p className="mb-2">You agree NOT to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Violate any laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Upload malicious code or viruses</li>
              <li>Attempt to gain unauthorized access</li>
              <li>Interfere with the Service's operation</li>
              <li>Use the Service for spam or unsolicited communications</li>
              <li>Impersonate others or provide false information</li>
              <li>Collect user data without consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">5. Data and Privacy</h2>
            <p>
              Your use of the Service is also governed by our Privacy Policy. By using the Service, 
              you consent to our collection and use of personal data as outlined in the Privacy Policy.
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>We process data in accordance with GDPR</li>
              <li>You retain ownership of your data</li>
              <li>You can export or delete your data at any time</li>
              <li>We implement security measures to protect your data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">6. Intellectual Property</h2>
            <p>
              The Service and its original content, features, and functionality are owned by Vinculum 
              and are protected by international copyright, trademark, and other intellectual property laws.
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>You retain rights to your uploaded content</li>
              <li>You grant us license to use your content to provide the Service</li>
              <li>You may not copy, modify, or distribute our intellectual property</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">7. AI-Powered Features</h2>
            <p>
              Our Service includes AI-powered enrichment features. You acknowledge that:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>AI-generated data may not always be accurate</li>
              <li>You should verify important information</li>
              <li>We are not liable for decisions based on AI-enriched data</li>
              <li>AI features are provided "as is" without warranties</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">8. Subscription and Payment</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Some features may require paid subscription</li>
              <li>Fees are billed in advance on a recurring basis</li>
              <li>Refunds are handled on a case-by-case basis</li>
              <li>We may change fees with 30 days notice</li>
              <li>Failure to pay may result in service suspension</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">9. Termination</h2>
            <p>
              We may terminate or suspend your account and access immediately, without prior notice, 
              for any breach of these Terms. Upon termination:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Your right to use the Service ceases immediately</li>
              <li>We may delete your data in accordance with our retention policy</li>
              <li>You may request data export before termination</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">10. Disclaimers</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. 
              We do not warrant that:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>The Service will be uninterrupted or error-free</li>
              <li>Defects will be corrected</li>
              <li>The Service is free from viruses or harmful components</li>
              <li>Results from using the Service will meet your requirements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">11. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Vinculum shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages resulting from your use of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">12. Changes to Terms</h2>
            <p>
              We reserve the right to modify these terms at any time. We will notify users of 
              significant changes. Continued use after changes constitutes acceptance of new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">13. Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with applicable laws, 
              including GDPR for EU users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-3">14. Contact</h2>
            <div className="mt-2 p-4 bg-gray-50 rounded-lg">
              <p><strong>Vinculum</strong></p>
              <p>Data Protection Officer: Kshitij Shah</p>
              <p className="mt-2">For questions about these Terms, please contact us through the support channels.</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;